import { Injectable } from '@angular/core'
import { environment } from '../../../../environments/environment'
import { HttpClient } from '@angular/common/http'
import { Observable } from 'rxjs'
import { ResponseRequestModel } from '../../models/response.model'
import {
  FornecedorMotoristaEdit,
  FornecedorMotoristaRegister,
  FornecedorVeiculoEdit,
  FornecedorVeiculoRegister,
} from '../../models/supplier-portal.model'

// Portal do Fornecedor — o token é injetado automaticamente pelo AuthInterceptor,
// o fornecedor é sempre resolvido pelo usuário logado (perfil 12; 13/1 liberados em qa).
@Injectable({
  providedIn: 'root',
})
export class SupplierPortalService {
  private baseUrl = environment.baseUrl
  private resource = 'FornecedorPortal'

  constructor(private httpClient: HttpClient) {}

  // Minhas OSs
  public listPagedOs(page: number, rows = 10): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/ordens-servico/listpaginated?pageNumber=${page}&pageSize=${rows}`,
    )
  }

  public getOsById(osId: string): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/ordens-servico/${osId}`,
    )
  }

  // Motorista
  public getMotoristaByCpf(cpf: string): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/por-cpf/${cpf}`,
    )
  }

  public createMotorista(model: FornecedorMotoristaRegister): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas`,
      model,
    )
  }

  public vincularMotorista(motoristaId: number): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/${motoristaId}/vincular`,
      {},
    )
  }

  public listPagedMotoristas(page: number, rows = 10): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/listpaginated?pageNumber=${page}&pageSize=${rows}`,
    )
  }

  public editMotorista(motoristaId: number, model: FornecedorMotoristaEdit): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/${motoristaId}/edit`,
      model,
    )
  }

  public listMotoristaDocumentos(motoristaId: number): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/${motoristaId}/documentos`,
    )
  }

  // Content-Type não é setado manualmente — o browser define o boundary do multipart.
  public uploadMotoristaDocumento(motoristaId: number, formData: FormData): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/${motoristaId}/documentos/upload`,
      formData,
    )
  }

  public downloadMotoristaDocumento(motoristaId: number, documentoId: number): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/${motoristaId}/documentos/${documentoId}/download`,
    )
  }

  public deleteMotoristaDocumento(motoristaId: number, documentoId: number): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/motoristas/${motoristaId}/documentos/${documentoId}/delete`,
      {},
    )
  }

  // Veículo
  public listPagedVeiculos(page: number, rows = 10): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos/listpaginated?pageNumber=${page}&pageSize=${rows}`,
    )
  }

  public createVeiculo(model: FornecedorVeiculoRegister): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos`,
      model,
    )
  }

  public editVeiculo(veiculoId: string, model: FornecedorVeiculoEdit): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos/${veiculoId}/edit`,
      model,
    )
  }

  public listVeiculoDocumentos(veiculoId: string): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos/${veiculoId}/documentos`,
    )
  }

  public uploadVeiculoDocumento(veiculoId: string, formData: FormData): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos/${veiculoId}/documentos/upload`,
      formData,
    )
  }

  public downloadVeiculoDocumento(veiculoId: string, documentoId: number): Observable<ResponseRequestModel> {
    return this.httpClient.get<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos/${veiculoId}/documentos/${documentoId}/download`,
    )
  }

  public deleteVeiculoDocumento(veiculoId: string, documentoId: number): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/veiculos/${veiculoId}/documentos/${documentoId}/delete`,
      {},
    )
  }

  // Atribuição na OS
  public atribuirMotorista(osId: string, motoristaId: number): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/ordens-servico/${osId}/motorista?motoristaId=${motoristaId}`,
      {},
    )
  }

  public atribuirVeiculo(osId: string, veiculoId: string): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/ordens-servico/${osId}/veiculo?veiculoId=${veiculoId}`,
      {},
    )
  }

  public editObservacao(osId: string, obsFornecedor: string): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/ordens-servico/${osId}/observacao`,
      { obsFornecedor },
    )
  }

  // Ação explícita — atribuir motorista/veículo não move a OS sozinho, é pré-requisito.
  // Idempotente: chamar de novo com a OS já em 12/5 devolve 200 com mensagem informativa.
  public confirmarOs(osId: string): Observable<ResponseRequestModel> {
    return this.httpClient.post<ResponseRequestModel>(
      `${this.baseUrl}${this.resource}/ordens-servico/${osId}/confirmar`,
      {},
    )
  }
}
